import type { User } from '@iheartjane/data-access';
import { EventNames } from '@iheartjane/tracking';
import type { Nullish } from '@iheartjane/types';

import type { PaymentOptions } from './hooks/usePaymentOptions';
import { PaymentMethods } from './hooks/usePaymentOptions';

/**
 * The decisions the checkout submit chain makes, as pure functions. Submitting
 * runs three stages — recaptcha, then MFA, then the checkout request — and each
 * gate below answers whether its stage can be skipped.
 */

// A switch rather than a lookup map: it keeps the return type narrowed to the
// two events `track` accepts, where a Record widens it to all of EventNames.
export const trackingEventForPayment = (selected: Nullish<PaymentOptions>) => {
  switch (selected) {
    case PaymentMethods.janePay:
      return EventNames.ClickedPayWithJanePay;
    case PaymentMethods.stronghold:
      return EventNames.ClickedPayWithStronghold;
    default:
      return null;
  }
};

/**
 * Providers that run their own fraud checks, so a second recaptcha adds friction
 * without adding protection — and, conversely, the providers we re-verify the
 * shopper for when their last MFA attempt has gone stale.
 */
const SELF_GUARDED_PAYMENTS: PaymentOptions[] = [
  PaymentMethods.janePay,
  PaymentMethods.stronghold,
];

export const shouldSkipRecaptcha = ({
  canpayV2RemotepayEnabled,
  isHeadlessKioskMode,
  selectedPaymentOption,
}: {
  canpayV2RemotepayEnabled: Nullish<boolean>;
  isHeadlessKioskMode: boolean;
  selectedPaymentOption: Nullish<PaymentOptions>;
}) => {
  // Kiosks are physically supervised, and the backend skips verification too.
  if (isHeadlessKioskMode) return true;
  if (!selectedPaymentOption) return false;
  if (SELF_GUARDED_PAYMENTS.includes(selectedPaymentOption)) return true;

  return (
    selectedPaymentOption === PaymentMethods.canpay &&
    Boolean(canpayV2RemotepayEnabled)
  );
};

/**
 * External users verify their phone with their own identity provider, so Jane
 * never asks them for a code.
 * @todo An absent `janeUser` reads as unverified, which means an authenticated
 * shopper sees MFA when /users/me has not resolved yet. Preserved as-is here;
 * fixing it is a behavior change to the live checkout flow.
 */
export const phoneNeedsVerification = (user: Nullish<User>) =>
  !user?.phone_verified_at && !user?.external_user;

/**
 * Three independent reasons to launch MFA before submitting: the shopper typed a
 * new number, an authenticated shopper has never verified the one on file, or an
 * authenticated shopper is prepaying and their last verification has gone stale.
 * Only the last is "extraneous" enough to be flag-disabled.
 */
export const shouldLaunchMfa = ({
  authenticated,
  hasNewPhone,
  mfaAtCheckoutDisabled,
  needsPhoneVerification,
  requiresMfa,
  selectedPaymentOption,
}: {
  authenticated: Nullish<boolean>;
  hasNewPhone: Nullish<boolean>;
  mfaAtCheckoutDisabled: boolean;
  needsPhoneVerification: boolean;
  requiresMfa: boolean;
  selectedPaymentOption: Nullish<PaymentOptions>;
}) => {
  if (hasNewPhone) return true;
  if (!authenticated) return false;
  if (needsPhoneVerification) return true;
  if (mfaAtCheckoutDisabled || !selectedPaymentOption) return false;

  return requiresMfa && SELF_GUARDED_PAYMENTS.includes(selectedPaymentOption);
};
